// src/pages/StaffApp.js
// Staff (cashier / manager) login + dashboard. Served at /staff.
//   CASHIER : work orders, mark items sold-out / adjust stock
//   MANAGER : + edit menu item name & price
// The server enforces these roles; the UI only hides what would be refused.

import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import staffApi, {
  staffLogin, getStaffSession, saveStaffSession, clearStaffSession,
} from '../services/staffApi';

const STATUS_NEXT  = { PENDING: 'PREPARING', PREPARING: 'READY', READY: 'COMPLETED' };
const STATUS_STYLE = {
  AWAITING_PAYMENT: { badge: 'badge-amber', label: 'Awaiting Payment' },
  PENDING:   { badge: 'badge-blue',  label: 'Pending'   },
  PREPARING: { badge: 'badge-amber', label: 'Preparing' },
  READY:     { badge: 'badge-green', label: 'Ready'     },
  COMPLETED: { badge: 'badge-green', label: 'Completed' },
  CANCELLED: { badge: 'badge-red',   label: 'Cancelled' },
};
const canEditMenu = (role) => role === 'OWNER' || role === 'MANAGER';
const errMsg = (err, fb) => err.response?.data?.error || fb;

// ── Login ─────────────────────────────────────────────────────
function StaffLogin({ onLoggedIn }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return setError('Enter your email and password.');
    setLoading(true); setError('');
    try {
      const r = await staffLogin(email.trim().toLowerCase(), password);
      saveStaffSession(r.data);
      onLoggedIn(r.data);
    } catch (err) {
      setError(err.response?.status === 429 ? 'Too many attempts — try again later.' : errMsg(err, 'Login failed'));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <form onSubmit={submit} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 40, width: 380, maxWidth: '92vw' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🧾</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>Staff Login</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>Cashiers &amp; managers</div>
        </div>
        <div className="section-label" style={{ marginBottom: 6 }}>Email</div>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus style={{ marginBottom: 16 }} />
        <div className="section-label" style={{ marginBottom: 6 }}>Password</div>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={{ marginBottom: 20 }} />
        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 14, textAlign: 'center' }}>{error}</div>}
        <button className="btn btn-primary" type="submit" style={{ width: '100%', padding: 13 }} disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <a href="/" style={{ fontSize: 12, color: 'var(--text3)' }}>← Owner / admin login</a>
        </div>
      </form>
    </div>
  );
}

// ── Orders ────────────────────────────────────────────────────
function StaffOrders({ flash, onSessionExpired }) {
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [orders, setOrders]   = useState(null);
  const [filter, setFilter]   = useState('ACTIVE');

  const load = useCallback(async () => {
    try {
      const r = await staffApi.get('/orders', { params: { limit: 100 } });
      setOrders(r.data);
    } catch (err) {
      if (err.response?.status === 401) return onSessionExpired();
      flash('❌ ' + errMsg(err, 'Could not load orders'));
    }
  }, [flash, onSessionExpired]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const change = async (o, status) => {
    try {
      await staffApi.patch(`/orders/${o.id}/status`, { status });
      setOrders(prev => prev.map(x => x.id === o.id ? { ...x, status } : x));
    } catch (err) { flash('❌ ' + errMsg(err, 'Update failed')); }
  };
  const cancel = async (o) => {
    try {
      await staffApi.patch(`/orders/${o.id}/cancel`);
      setOrders(prev => prev.map(x => x.id === o.id ? { ...x, status: 'CANCELLED' } : x));
    } catch (err) { flash('❌ ' + errMsg(err, 'Cancel failed')); }
  };

  if (!orders) return <div className="card">Loading orders...</div>;
  const shown = orders.filter(o => filter === 'ALL' ? true : ['PENDING', 'PREPARING', 'READY'].includes(o.status));

  return (
    <>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[['ACTIVE', 'Active'], ['ALL', 'All recent']].map(([id, label]) => (
          <button key={id} className={`btn btn-sm ${filter === id ? 'btn-primary' : ''}`} onClick={() => setFilter(id)}>{label}</button>
        ))}
        <button className="btn btn-sm" onClick={load}>↻ Refresh</button>
      </div>
      {shown.length === 0 && <div className="card" style={{ color: 'var(--text3)', textAlign: 'center' }}>No orders</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
        {shown.map(o => {
          const ss = STATUS_STYLE[o.status] || { badge: 'badge-amber', label: o.status };
          const next = STATUS_NEXT[o.status];
          return (
            <div key={o.id} className="card" style={{ opacity: o.status === 'CANCELLED' ? .55 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong style={{ color: 'var(--accent)' }}>{o.orderNumber}</strong>
                <span className={`badge ${ss.badge}`}>{ss.label}</span>
              </div>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                {(o.items || []).map((i, k) => <div key={k}>{i.quantity || i.qty}× {i.name}</div>)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
                <span>AED {Number(o.total).toFixed(2)} · {o.paymentStatus}</span>
                <span>{new Date(o.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {next && <button className="btn btn-primary btn-sm" onClick={() => change(o, next)}>→ {STATUS_STYLE[next].label}</button>}
                {['PENDING', 'PREPARING', 'READY', 'AWAITING_PAYMENT'].includes(o.status) &&
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ message: `Cancel order ${o.orderNumber}?`, confirmLabel: 'Cancel order', onConfirm: () => cancel(o) })}>Cancel</button>}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Menu ──────────────────────────────────────────────────────
function StaffMenu({ vendorId, role, flash, onSessionExpired }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit]   = useState({}); // itemId -> { name, price }
  const editable = canEditMenu(role);

  const load = useCallback(async () => {
    try {
      const r = await staffApi.get('/menu', { params: { vendorId } });
      setItems(r.data);
    } catch (err) {
      if (err.response?.status === 401) return onSessionExpired();
      flash('❌ ' + errMsg(err, 'Could not load menu'));
      setItems([]);
    }
  }, [vendorId, flash, onSessionExpired]);
  useEffect(() => { load(); }, [load]);

  const patchItem = (id, patch) => setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));

  const toggle = async (i) => {
    try { const r = await staffApi.patch(`/menu/${i.id}/toggle`); patchItem(i.id, { isActive: r.data.isActive }); }
    catch (err) { flash('❌ ' + errMsg(err, 'Update failed')); }
  };
  const setStock = async (i, stock) => {
    const n = parseInt(stock);
    if (isNaN(n) || n < 0) return flash('❌ Enter a valid stock number');
    try { await staffApi.post(`/menu/${i.id}/stock`, { stock: n }); patchItem(i.id, { stock: n }); flash('✅ Stock updated'); }
    catch (err) { flash('❌ ' + errMsg(err, 'Update failed')); }
  };
  const saveEdit = async (i) => {
    const e = edit[i.id]; if (!e) return;
    const price = parseFloat(e.price);
    if (!e.name?.trim() || isNaN(price) || price < 0) return flash('❌ Enter a name and a valid price');
    try {
      await staffApi.put(`/menu/${i.id}`, { name: e.name.trim(), price });
      patchItem(i.id, { name: e.name.trim(), price });
      setEdit(prev => { const n = { ...prev }; delete n[i.id]; return n; });
      flash('✅ Saved');
    } catch (err) { flash('❌ ' + errMsg(err, 'Save failed')); }
  };

  if (!items) return <div className="card">Loading menu...</div>;
  if (!items.length) return <div className="card" style={{ color: 'var(--text3)', textAlign: 'center' }}>No menu items</div>;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {items.map(i => {
        const e = edit[i.id];
        return (
          <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', opacity: i.isActive ? 1 : .6 }}>
            <span style={{ fontSize: 22 }}>{i.emoji || '🍽'}</span>
            <div style={{ flex: 1, minWidth: 140 }}>
              {e ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={e.name} onChange={ev => setEdit(p => ({ ...p, [i.id]: { ...e, name: ev.target.value } }))} />
                  <input type="number" step="0.01" value={e.price} style={{ width: 90 }} onChange={ev => setEdit(p => ({ ...p, [i.id]: { ...e, price: ev.target.value } }))} />
                </div>
              ) : (
                <>
                  <div style={{ fontWeight: 600 }}>{i.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>AED {Number(i.price).toFixed(2)} · {i.category}</div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              Stock
              <input type="number" min="0" defaultValue={i.stock} key={`${i.id}-${i.stock}`} style={{ width: 70 }}
                onBlur={ev => String(i.stock) !== ev.target.value && setStock(i, ev.target.value)} />
            </div>
            <button className={`btn btn-sm ${i.isActive ? '' : 'btn-primary'}`} onClick={() => toggle(i)}>
              {i.isActive ? 'Mark sold out' : 'Make available'}
            </button>
            {editable && (e
              ? <><button className="btn btn-primary btn-sm" onClick={() => saveEdit(i)}>Save</button>
                  <button className="btn btn-sm" onClick={() => setEdit(p => { const n = { ...p }; delete n[i.id]; return n; })}>Cancel</button></>
              : <button className="btn btn-sm" onClick={() => setEdit(p => ({ ...p, [i.id]: { name: i.name, price: i.price } }))}>Edit</button>)}
          </div>
        );
      })}
    </div>
  );
}

// ── Shell ─────────────────────────────────────────────────────
export default function StaffApp() {
  const [session, setSession] = useState(() => getStaffSession());
  const [tab, setTab]         = useState('orders');
  const [msg, setMsg]         = useState('');

  const flash = useCallback((text) => { setMsg(text); setTimeout(() => setMsg(''), 3500); }, []);
  const logout = useCallback(() => { clearStaffSession(); setSession(null); }, []);

  if (!session?.token) return <StaffLogin onLoggedIn={setSession} />;

  const { staff, vendor } = session;
  return (
    <div>
      <div className="nav" style={{ flexWrap: 'wrap', gap: 6 }}>
        <div className="nav-logo">⚡ {vendor?.name}</div>
        <div className="nav-tabs">
          {[['orders', '🧾 Orders'], ['menu', '🍽 Menu']].map(([id, label]) => (
            <button key={id} className={`nav-tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{staff?.name || staff?.email}</div>
          <span className="badge badge-amber">{staff?.role}</span>
          <button className="btn btn-ghost btn-sm" onClick={logout}>Logout</button>
        </div>
      </div>
      {vendor?.status === 'SUSPENDED' && (
        <div style={{ background: '#3d1a1a', color: 'var(--red)', padding: '10px 16px', fontSize: 13, fontWeight: 600 }}>
          🔴 This store is suspended — you can view data but not make changes.
        </div>
      )}
      <div className="page">
        {msg && <div className="card" style={{ marginBottom: 14, padding: '10px 14px' }}>{msg}</div>}
        {tab === 'orders' && <StaffOrders flash={flash} onSessionExpired={logout} />}
        {tab === 'menu'   && <StaffMenu vendorId={staff?.vendorId || vendor?.id} role={staff?.role} flash={flash} onSessionExpired={logout} />}
      </div>
    </div>
  );
}
